<template>
  <v-card
    elevation="0"
    :loading="loading || internalLoading || saving"
    :disabled="loading || internalLoading || saving"
  >
    <v-card-title class="justify-center">
      <span v-if="level_id == 1 && sub_level_id == null">
        {{ $t("payment.system_undergraduate") }}
      </span>
      <span v-else-if="level_id == 1 && sub_level_id == 1">
        {{ $t("payment.system_4+1") }}
      </span>
      <span v-else>
        {{ $t("payment.system_graduate") }}
      </span>
    </v-card-title>
    <v-card-text class="px-2">
      <v-simple-table dense class="table mx-auto">
        <tr>
          <th class="header-nowrap">
            {{ $t("payment.year") }}
          </th>
          <th>
            {{ $t("payment.transaction") }}
          </th>
          <th>
            <span class="header-line">{{ $t("payment.tuition_fee_header_1") }}</span>
            <span class="header-line">{{ $t("payment.tuition_fee_header_2") }}</span>
          </th>
          <th>
            <span class="header-line">{{ $t("payment.discount_header_1") }}</span>
            <span class="header-line">{{ $t("payment.discount_header_2") }}</span>
          </th>
          <th>
            <span class="header-line">{{ $t("payment.other_fee_header_1") }}</span>
            <span class="header-line">{{ $t("payment.other_fee_header_2") }}</span>
          </th>
          <th>
            <span class="header-line">{{ $t("payment.late_chart_header_1") }}</span>
            <span class="header-line">{{ $t("payment.late_chart_header_2") }}</span>
          </th>
          <th>
            <span class="header-line">{{ $t("payment.pay_chart_header_1") }}</span>
            <span class="header-line">{{ $t("payment.pay_chart_header_2") }}</span>
          </th>
          <th>
            {{ $t("payment.paid") }}
          </th>
          <th>
            {{ $t("payment.remain") }}
          </th>
          <th class="last-cell">
            {{ $t("payment.print") }}
          </th>
        </tr>
        <tr v-for="(item, index) in transaction_list" :key="index">
          <td>{{ item.semester }} / {{ item.year }}</td>
          <td>
            {{ objectTranslatorLowerCase(item, "regis_type_txt") }}
          </td>
          <td class="text-right">
            <div
              v-if="inline_edit_key === row_key(item)"
              class="inline-fee-editor"
            >
              <v-text-field
                v-model="inline_tuition_fee"
                class="inline-fee-input"
                type="number"
                min="0"
                step="1"
                inputmode="numeric"
                dense
                outlined
                hide-details
                autofocus
                @keyup.enter="request_tuition_update(item)"
                @keyup.esc="cancel_tuition_edit"
              />
              <v-btn
                icon
                x-small
                color="success"
                :disabled="!is_valid_amount(inline_tuition_fee)"
                :title="$t('payment.save')"
                @click="request_tuition_update(item)"
              >
                <v-icon small>mdi-content-save</v-icon>
              </v-btn>
              <v-btn
                icon
                x-small
                color="error"
                :title="$t('payment.cancel')"
                @click="cancel_tuition_edit"
              >
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
            <div v-else class="fee-display">
              <span>{{ item.tuition_fee }}</span>
              <v-btn
                v-if="can_edit_tuition(item)"
                icon
                x-small
                color="primary"
                :title="tuition_edit_title(item)"
                @click="start_tuition_edit(item)"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </div>
          </td>
          <td :class="getValue('special', item.special_fee2)">
            {{ item.special_fee2 }}
          </td>
          <td :class="getValue('special', item.sum_other_fee)">
            <div class="fee-display">
              <span>{{ item.sum_other_fee }}</span>
              <v-btn
                v-if="
                  !(level_id === 1 && sub_level_id === 1) &&
                  raw_amount(item.sum_other_fee) !== 0
                "
                icon
                x-small
                color="orange darken-2"
                :title="$t('payment.view_other_fees')"
                @click="open_edit_dialog('other', item, true)"
              >
                <v-icon small>mdi-eye-outline</v-icon>
              </v-btn>
              <v-btn
                v-if="fee_editable && !(level_id === 1 && sub_level_id === 1)"
                icon
                x-small
                color="primary"
                :title="$t('payment.edit_other_fees')"
                @click="open_edit_dialog('other', item, false)"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </div>
          </td>
          <td :class="getValue('charge', item.late_charge)">
            <div class="fee-display">
              <span>{{ item.late_charge }}</span>
              <v-btn
                v-if="fee_editable"
                icon
                x-small
                color="primary"
                :title="$t('payment.edit_late_charge')"
                @click="open_edit_dialog('late_charge', item)"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </div>
          </td>
          <td :class="getValue('charge', item.pay_charge)">
            <div class="fee-display">
              <span>{{ item.pay_charge }}</span>
              <v-tooltip
                v-if="show_first_year_first_semester_pay_charge_info(index)"
                top
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="info"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-information-outline
                  </v-icon>
                </template>
                <span>
                  {{ $t("payment.first_year_first_semester_no_pay_charge") }}
                </span>
              </v-tooltip>
              <v-btn
                v-if="fee_editable"
                icon
                x-small
                color="primary"
                :title="$t('payment.edit_pay_charge')"
                @click="open_edit_dialog('pay_charge', item)"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </div>
          </td>
          <td :class="getValue('paid', item.paid)">
            <span>{{ item.paid }}</span>
            <v-btn
              v-if="editable || raw_amount(item.paid) > 0"
              icon
              x-small
              color="#ff5722"
              :title="$t('payment.view_payment_details')"
              @click="open_billing_details(item)"
            >
              <v-icon x-small>mdi-eye-outline</v-icon>
            </v-btn>
          </td>
          <td :class="getValue('remain', item.remain)">
            {{ item.remain }}
          </td>
          <td v-if="item.error" class="red--text" style="max-width: 150px; white-space: wrap;">
            {{
              typeof item.error === "object"
                ? objectTranslatorLowerCase(item.error, "message")
                : item.error
            }}
          </td>
          <td
            class="disflex flex-column align-center text-center last-cell pa-2"
            v-else-if="item.remain != '0.00' && payment_actions_enabled"
          >
            <!-- ชำระด้วย QR Code -->
            <v-btn
              class="mb-1 pa-2"
              small
              :to="{
                path:
                  `/payment_slip?year=${item.year}&semester=${item.semester}&student_id=${student_id}&level_id=${level_id}` +
                  (sub_level_id ? `&sub_level_id=${sub_level_id}` : ``) +
                  (student_mode ? `&as_student=1` : ``),
              }"
              target="_blank"
            >
              <v-icon class="gradient-text pr-2"> mdi-qrcode-scan </v-icon>QR
              Code
            </v-btn>
            <!-- Icon อยู่คนละบรรทัด โดย Icon แรกมีข้อความบอกว่า ชำระด้วย QR Code ส่วน Icon ที่สองมีข้อความบอกว่า ชำระด้วยบัตร -->
            <!-- ชำระด้วยบัตร -->
            <v-btn
              small
              @click="
                submitForm(
                  index,
                  item.bank_info_id,
                  item.system_id,
                  item.pay_for_id,
                  item.year,
                  item.semester,
                  item.level_type_in_payment,
                  item.reserved,
                  item.remain
                )
              "
              class="pa-2"
              target="_blank"
            >
              <v-icon class="gradient-text pr-1"> mdi-credit-card </v-icon
              >{{ $t("payment.credit_card") }}
            </v-btn>
            <div>
              <form
                :id="'paymentForm' + index"
                method="post"
                :action="fastpay_url"
              >
                <input type="hidden" name="merchantId" />
                <input type="hidden" name="amount" />
                <input type="hidden" name="orderRef" />
                <input type="hidden" name="currCode" />
                <input type="hidden" name="securityKey" />
                <input type="hidden" name="successUrl" :value="redirect_url" />
                <input type="hidden" name="failUrl" :value="redirect_url" />
                <input type="hidden" name="cancelUrl" :value="redirect_url" />
                <input type="hidden" name="payType" />
                <input
                  type="hidden"
                  name="lang"
                  :value="
                    inlineTranslator({
                      en: 'E',
                      th: 'T',
                    })
                  "
                />
                <input type="hidden" name="redirect" value="30" />
                <input type="hidden" name="orderRef1" />
                <input type="hidden" name="orderRef2" />
                <input type="hidden" name="remark" />
              </form>
            </div>
          </td>
          <td v-else-if="item.remain != '0.00'" class="text-center last-cell">
            <v-icon small color="grey" class="mr-1">mdi-eye-outline</v-icon>
            <span class="grey--text text--darken-1">
              {{ $t("payment.view_only") }}
            </span>
          </td>
          <td v-else class="text-center last-cell">
            <!-- ชำระเงินแล้ว -->
            <span class="green--text">{{ $t("payment.complete") }}</span>
            <v-icon
              v-if="item.payment_status == 'paid'"
              class="ml-1 gradient-text1"
            >
              mdi-shield-check
            </v-icon>
          </td>
        </tr>
      </v-simple-table>

      <div class="red--text text-center mt-3 mb-3">
        <div>
          {{ $t("payment.note_late_chart") }}
        </div>
        <div>
          {{ $t("payment.note_pay_chart_1") }}
          <span v-if="level_id == 1 && sub_level_id == null">50</span
          ><span v-else>100</span> {{ $t("payment.note_pay_chart_2") }}
        </div>
      </div>
      <v-divider></v-divider>

      <div class="mx-5 mt-3 mb-3">
        <v-icon class="amber--text">mdi-alert</v-icon>
        <span class="red--text">
          {{ $t("payment.notification") }}
        </span>
      </div>
      <div class="red--text">
        <div class="mx-10 mb-2">
          <li>{{ $t("payment.notification_1") }}</li>
        </div>
        <div class="mx-10 mb-2">
          <li>{{ $t("payment.notification_2") }}</li>
        </div>
        <div class="mx-10 mb-2">
          <li>{{ $t("payment.notification_3") }}</li>
        </div>
      </div>
      <div class="mx-5 mt-3 mb-3">
        <v-icon class="gradient-text1 pr-1"> mdi-shield-check </v-icon>
        {{ $t("payment.billing") }}
        <v-btn
          class="ma-2"
          outlined
          rounded
          color="indigo"
          href="https://www.reg.kmitl.ac.th/index?f=payment&p=print_receipt.php"
          target="_blank"
        >
          {{ $t("payment.request_receipt") }}
          <v-icon class="pl-1"> mdi-open-in-new </v-icon></v-btn
        >
      </div>
    </v-card-text>

    <PaymentBillingDetailsDialog
      v-model="billing_details_dialog"
      :item="billing_details_item"
      :student_id="student_id"
      :student_name="student_name"
      :level_id="level_id"
      :sub_level_id="sub_level_id"
      :editable="editable"
      @updated="$emit('updated')"
    />
    <PaymentFeeEditDialog
      v-model="edit_dialog"
      :type="edit_dialog_type"
      :item="edit_dialog_item"
      :level_id="level_id"
      :loading="saving"
      :readonly="edit_dialog_readonly"
      @save="request_dialog_update"
    />
    <PaymentFeeConfirmDialog
      :value="confirm_dialog"
      :loading="saving"
      :student_id="student_id"
      :student_name="student_name"
      :level_name="confirmation_level_name"
      :registration_type="confirmation_registration_type"
      :item="confirmation_item"
      :changes="confirmation_changes"
      @cancel="cancel_confirmation"
      @confirm="confirm_update"
    />
  </v-card>
</template>
<script>
import payInSlipProvider from "@/services/pay_in_slip.provider";
import {
  inlineTranslator,
  objectTranslatorLowerCase,
} from "@/helper/translator";
import PaymentFeeEditDialog from "./PaymentFeeEditDialog.vue";
import PaymentFeeConfirmDialog from "./PaymentFeeConfirmDialog.vue";
import PaymentBillingDetailsDialog from "./PaymentBillingDetailsDialog.vue";

const payInSlipService = new payInSlipProvider();
export default {
  name: "PaymentTable",
  components: {
    PaymentBillingDetailsDialog,
    PaymentFeeEditDialog,
    PaymentFeeConfirmDialog,
  },
  props: {
    student_id: {
      type: String,
      required: true,
    },
    student_name: {
      type: String,
      default: "",
    },
    student_mode: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    fee_editable: {
      type: Boolean,
      default: false,
    },
    payment_actions_enabled: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
    },
    transaction_list: {
      type: Array,
      default: null,
    },
    level_id: {
      type: Number,
    },
    sub_level_id: {
      type: Number,
    },
  },
  data: () => ({
    merchant_id: null,
    amount: null,
    order_ref: null,
    curr_code: null,
    security_key: null,
    paytype: null,
    language: null,
    ref_1: null,
    ref_2: null,
    remark: null,
    internalLoading: false,
    saving: false,
    inline_edit_key: null,
    inline_tuition_fee: "",
    edit_dialog: false,
    edit_dialog_type: null,
    edit_dialog_item: null,
    edit_dialog_readonly: false,
    confirm_dialog: false,
    pending_update: null,
    billing_details_dialog: false,
    billing_details_item: null,
  }),
  methods: {
    objectTranslatorLowerCase,
    inlineTranslator,

    row_key(item) {
      return `${item.year}-${item.semester}`;
    },
    open_billing_details(item) {
      this.billing_details_item = item;
      this.billing_details_dialog = true;
    },
    show_first_year_first_semester_pay_charge_info(index) {
      return index === 0 && this.sub_level_id !== 1;
    },
    raw_amount(value) {
      const amount = Number(String(value ?? "0").replace(/,/g, ""));
      return Number.isFinite(amount) ? Math.trunc(amount) : 0;
    },
    is_valid_amount(value) {
      return /^\d+$/.test(String(value ?? ""));
    },
    can_edit_tuition(item) {
      return this.fee_editable && ["S", "M", "D"].includes(item.regis_type);
    },
    tuition_edit_title(item) {
      if (item.regis_type === "M") return this.$t("payment.edit_maintain_fee");
      if (item.regis_type === "D") return this.$t("payment.edit_leave_fee");
      return this.$t("payment.edit_tuition_fee");
    },
    start_tuition_edit(item) {
      this.inline_edit_key = this.row_key(item);
      this.inline_tuition_fee = String(this.raw_amount(item.tuition_fee));
    },
    cancel_tuition_edit() {
      this.inline_edit_key = null;
      this.inline_tuition_fee = "";
    },
    payment_reference(item) {
      return {
        level_id: this.level_id,
        sub_level_id: this.sub_level_id,
        student_id: this.student_id,
        year: item.year,
        semester: item.semester,
      };
    },
    error_message(error, fallback = this.$t("payment.update_failed")) {
      const message = error.response?.data?.message || error.message;
      if (typeof message === "object") {
        return this.objectTranslatorLowerCase(message, "message") || fallback;
      }
      return message || fallback;
    },
    request_tuition_update(item) {
      if (!this.is_valid_amount(this.inline_tuition_fee)) return;
      this.open_confirmation("tuition", item, {
        tuition_fee: Number(this.inline_tuition_fee),
      });
    },
    open_edit_dialog(type, item, readonly = false) {
      this.edit_dialog_type = type;
      this.edit_dialog_item = item;
      this.edit_dialog_readonly = readonly;
      this.edit_dialog = true;
    },
    request_dialog_update(values) {
      if (!this.edit_dialog_item) return;
      this.open_confirmation(
        this.edit_dialog_type,
        this.edit_dialog_item,
        values
      );
    },
    open_confirmation(type, item, values) {
      this.pending_update = { type, item, values };
      this.confirm_dialog = true;
    },
    cancel_confirmation() {
      if (this.saving) return;
      this.confirm_dialog = false;
      this.pending_update = null;
    },
    async confirm_update() {
      const hasChanges = this.confirmation_changes.some(
        (change) => String(change.old_value) !== String(change.new_value)
      );
      if (!this.pending_update || !hasChanges) return;

      this.saving = true;
      try {
        const { type, item, values } = this.pending_update;
        const reference = this.payment_reference(item);
        if (type === "tuition") {
          await payInSlipService.updatePaymentTuitionFee({
            ...reference,
            tuition_fee: values.tuition_fee,
          });
          this.cancel_tuition_edit();
        } else if (type === "other") {
          await payInSlipService.updatePaymentOtherFees({
            ...reference,
            ...values,
          });
        } else if (["late_charge", "pay_charge"].includes(type)) {
          await payInSlipService.updatePaymentPenaltyCharge({
            ...reference,
            charge_type: type,
            is_exempt: values.is_exempt,
          });
        }

        if (type !== "tuition") {
          this.edit_dialog = false;
          this.edit_dialog_item = null;
          this.edit_dialog_readonly = false;
        }
        this.confirm_dialog = false;
        this.pending_update = null;
        this.$toasted.success(this.$t("payment.update_success"));
        this.$emit("updated");
      } catch (error) {
        this.$toasted.error(this.error_message(error), { duration: 0 });
      } finally {
        this.saving = false;
      }
    },

    getValue(type, value) {
      if (value != "0.00") {
        if (type == "remain") {
          return "remainTextColor";
        } else if (type == "charge") {
          return "chargeTextColor";
        } else if (type == "paid") {
          return "paidTextColor";
        } else if (type == "special") {
          return "specialTextColor";
        } else {
          return "normal";
        }
      } else {
        return "normal";
      }
    },

    async submitForm(
      index,
      bank_info_id,
      system_id,
      pay_for_id,
      year,
      semester,
      level_payment_id,
      reserved,
      remain
    ) {
      this.internalLoading = true;

      const response = await payInSlipService
        .getFastpayOrder({
          ref_1: this.student_id,
          bank_info_id: bank_info_id,
          system_id: system_id,
          pay_for_id: pay_for_id,
          year: year,
          semester: semester,
          level_payment_id: level_payment_id,
          reserved: reserved,
          amount: parseInt(remain.replace(/,/g, "").split(".")[0], 10),
        })
        .catch((error) => {
          const errorMessage = this.error_message(
            error,
            this.$t("payment.load_error")
          );
          this.$toasted.error(errorMessage, {
            duration: 0,
          });
          this.internalLoading = false;
        });

      if (response?.error) {
        const responseError = response.error.message || response.error;
        const errorMessage =
          (typeof responseError === "object"
            ? this.objectTranslatorLowerCase(responseError, "message")
            : responseError) || this.$t("payment.load_error");
        this.$toasted.error(errorMessage, {
          duration: 0,
        });
        this.internalLoading = false;
        return;
      }
      if (response) {
        document.getElementsByName("merchantId")[0].value = response.MERCHANTID;
        document.getElementsByName("orderRef")[0].value = response.ORDERREF;
        document.getElementsByName("currCode")[0].value = response.CURRCODE;
        document.getElementsByName("amount")[0].value = response.AMOUNT;
        document.getElementsByName("payType")[0].value = response.PAYTYPE;
        document.getElementsByName("securityKey")[0].value =
          response.SECURITYKEY;
        document.getElementsByName("orderRef1")[0].value = response.ORDERREF1;
        document.getElementsByName("orderRef2")[0].value = response.ORDERREF2;
        document.getElementsByName("remark")[0].value = response.REMARK;

        await new Promise((resolve) => setTimeout(resolve, 0));
        const selectedForm = document.getElementById("paymentForm" + index);
        selectedForm.submit();
      }
      this.internalLoading = false;
    },
  },
  computed: {
    confirmation_item() {
      return this.pending_update?.item || null;
    },
    confirmation_level_name() {
      if (this.level_id === 1 && this.sub_level_id === 1) {
        return this.$t("payment.system_4+1");
      }
      if (this.level_id === 1) {
        return this.$t("payment.system_undergraduate");
      }
      return this.$t("payment.system_graduate");
    },
    confirmation_registration_type() {
      if (!this.confirmation_item) return "";
      return this.objectTranslatorLowerCase(
        this.confirmation_item,
        "regis_type_txt"
      );
    },
    confirmation_changes() {
      if (!this.pending_update) return [];

      const { type, item, values } = this.pending_update;
      if (type === "tuition") {
        return [
          {
            key: "tuition_fee",
            label:
              item.regis_type === "M"
                ? this.$t("payment.maintain_fee")
                : item.regis_type === "D"
                ? this.$t("payment.leave_fee")
                : this.$t("payment.tuition_fee"),
            old_value: this.raw_amount(item.tuition_fee),
            new_value: values.tuition_fee,
          },
        ];
      }
      if (type === "other") {
        if (this.level_id === 1) {
          return [
            {
              key: "subsidy_fee",
              label: this.$t("payment.subsidy_fee"),
              old_value: this.raw_amount(item.subsidy_fee),
              new_value: values.subsidy_fee,
            },
          ];
        }
        const changes = [
          {
            key: "thesis_fee",
            label: this.$t("payment.thesis_fee"),
            old_value: this.raw_amount(item.thesis_fee),
            new_value: values.thesis_fee,
          },
          {
            key: "compre_fee",
            label: this.$t("payment.compre_fee"),
            old_value: this.raw_amount(item.compre_fee),
            new_value: values.compre_fee,
          },
          {
            key: "qe_fee",
            label: this.$t("payment.qe_fee"),
            old_value: this.raw_amount(item.qe_fee),
            new_value: values.qe_fee,
          },
        ];
        changes.push({
          key: "other_fee_total",
          label: this.$t("payment.other_fee_total"),
          old_value: changes.reduce(
            (total, change) => total + change.old_value,
            0
          ),
          new_value: changes.reduce(
            (total, change) => total + change.new_value,
            0
          ),
        });
        return changes;
      }
      if (["late_charge", "pay_charge"].includes(type)) {
        const skipKey =
          type === "late_charge"
            ? "skip_late_charge"
            : "skip_pay_charge";
        const currentExempt = Boolean(item[skipKey]);
        const newExempt = Boolean(values.is_exempt);
        return [
          {
            key: type,
            label: this.$t(
              type === "late_charge" ? "payment.late_chart" : "payment.pay_chart"
            ),
            // API ค่าปรับแก้เฉพาะสถานะใน skip_charge เท่านั้น
            old_value: currentExempt ? 1 : 0,
            new_value: newExempt ? 1 : 0,
            old_display: this.$t(
              currentExempt
                ? "payment.penalty_status_exempt"
                : "payment.penalty_status_charge"
            ),
            new_display: this.$t(
              newExempt
                ? "payment.penalty_status_exempt"
                : "payment.penalty_status_charge"
            ),
          },
        ];
      }
      return [];
    },
    fastpay_url() {
      return process.env.VUE_APP_FASTPAY_FORM_URL;
    },
    redirect_url() {
      return process.env.VUE_APP_FASTPAY_REDIRECT_URL;
    },
  },
};
</script>
<style lang="scss" scoped>
th,
td {
  text-align: center;
  padding: 1px 5px 1px 5px;
}
td {
  white-space: nowrap;
}
th {
  background-color: #14438a;
  color: rgb(255, 255, 255);
  white-space: normal;
  overflow-wrap: normal;
  word-break: normal;
  line-height: 1.2;
  padding-top: 6px;
  padding-bottom: 6px;
}
.header-nowrap,
.header-line {
  white-space: nowrap;
}
.header-line {
  display: block;
}
.table {
  max-width: 1300px;
  box-shadow: 1px 2px 5px 0px rgba(0, 0, 0, 0.3);
}
.table tr {
  line-height: 30px;
}
.last-cell {
  border-right-width: 1px;
}
.normal {
  color: rgb(200, 200, 200);
  text-align: right;
}
.disflex {
  display: flex;
}
.fee-display {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
}
.inline-fee-editor {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  min-width: 150px;
}
.inline-fee-input {
  max-width: 100px;
}
.specialTextColor {
  color: rgb(0, 0, 0);
  text-align: right;
}
.chargeTextColor {
  color: rgb(216, 36, 36);
  text-align: right;
}
.remainTextColor {
  color: rgb(216, 36, 36);
  text-align: right;
  font-weight: bold;
}
.paidTextColor {
  color: rgb(14, 155, 211);
  text-align: right;
}
.gradient-text {
  text-align: center;
  background: #6200ff;
  background: -webkit-gradient(
    linear,
    left top,
    right top,
    from(#6200ff),
    color-stop(50%, #ff005b),
    to(#6200ff)
  );
  background: linear-gradient(90deg, #6200ff 0%, #ff005b 50%, #6200ff 100%);
  background-size: 200% auto;
  color: #000;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  -webkit-animation: shine 1s linear infinite;
  animation: shine 1s linear infinite;
}

.gradient-text1 {
  background: linear-gradient(90deg, #000000 0%, #d6d6d6 50%, #000000 100%);
  background-size: 300% 300%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  -webkit-animation: shine1 4s ease-out infinite;
  animation: shine1 4s ease-out infinite;
}

// @-webkit-keyframes shine1 {
//   from {
//     background-position: top right;
//   }
//   to {
//     background-position: bottom left;
//   }
// }
// @keyframes shine1 {
//   from {
//     background-position: top right;
//   }
//   to {
//     background-position: bottom left;
//   }
// }
@-webkit-keyframes shine1 {
  0% {
    background-position: top right;
  }
  25% {
    background-position: bottom left;
  }
  100% {
    background-position: bottom left;
  }
}
@keyframes shine1 {
  0% {
    background-position: top right;
  }
  25% {
    background-position: bottom left;
  }
  100% {
    background-position: bottom left;
  }
}

@-webkit-keyframes shine {
  to {
    background-position: 200% center;
  }
}

@keyframes shine {
  to {
    background-position: 200% center;
  }
}
</style>
